import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Pause } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, EASE } from "./motion";

const SLIDE_BASIS = "basis-[82%] sm:basis-[62%] lg:basis-[48%]";
const AUTOPLAY_MS = 5000;

export const FieldsCarousel = () => {
  const { t } = useLanguage();
  const f = t.focus;
  const items = f.items;
  const n = items.length;
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);
  const heldRef = useRef(false);
  const progressRef = useRef(0);
  const barRef = useRef(null);
  const draggedRef = useRef(false);
  const reduce = useReducedMotion();
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [metrics, setMetrics] = useState({ vw: 0, sw: 0 });

  const goTo = useCallback(
    (i) => {
      progressRef.current = 0;
      setActive(((i % n) + n) % n);
    },
    [n]
  );
  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    if (reduce) return;
    let raf;
    let last = performance.now();
    const loop = (now) => {
      const dt = now - last;
      last = now;
      if (!heldRef.current && !draggedRef.current) {
        progressRef.current += dt / AUTOPLAY_MS;
        if (progressRef.current >= 1) {
          progressRef.current = 0;
          nextRef.current();
        }
        if (barRef.current) barRef.current.style.width = `${progressRef.current * 100}%`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduce]);

  useEffect(() => {
    const onGoto = (e) => goTo(e.detail);
    window.addEventListener("df:field", onGoto);
    return () => window.removeEventListener("df:field", onGoto);
  }, [goTo]);

  useEffect(() => {
    const measure = () => {
      const slide = trackRef.current?.children?.[0];
      if (slide && viewportRef.current) {
        setMetrics({ vw: viewportRef.current.offsetWidth, sw: slide.offsetWidth });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const x = metrics.sw ? (metrics.vw - metrics.sw) / 2 - active * metrics.sw : 0;

  const startHold = () => {
    heldRef.current = true;
    setHeld(true);
  };
  const endHold = () => {
    heldRef.current = false;
    setHeld(false);
  };

  const onSlideDown = (i) => {
    if (i === active) startHold();
  };
  const onSlideUp = (i) => {
    endHold();
    if (draggedRef.current) return;
    if (i !== active) goTo(i);
  };

  return (
    <section
      id="arbeitsfelder"
      data-testid="focus-section"
      className="grain relative overflow-hidden bg-[#032C37] text-white"
    >
      <div className="mx-auto max-w-[1600px] px-6 pt-24 sm:px-12 sm:pt-32 md:px-16">
        <Reveal y={14}>
          <p className="text-[13px] font-medium uppercase tracking-label text-wave">{f.label}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="font-head mt-6 max-w-3xl text-3xl leading-tight sm:text-5xl">{f.title}</h2>
        </Reveal>
      </div>

      <div ref={viewportRef} data-testid="fields-carousel" className="mt-10 overflow-hidden sm:mt-14">
        <motion.div
          ref={trackRef}
          className="flex cursor-grab active:cursor-grabbing"
          drag={reduce ? false : "x"}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.12}
          onDragStart={() => {
            draggedRef.current = true;
            endHold();
          }}
          onDragEnd={(e, info) => {
            if (info.offset.x < -60) goTo(active + 1);
            else if (info.offset.x > 60) goTo(active - 1);
            setTimeout(() => (draggedRef.current = false), 60);
          }}
          animate={{ x }}
          transition={{ duration: 1.15, ease: EASE }}
        >
          {items.map((item, i) => (
            <div key={item.to} data-testid={`field-slide-${i}`} className={`${SLIDE_BASIS} shrink-0 px-2 sm:px-3`}>
              <div
                role="button"
                tabIndex={0}
                aria-label={item.title}
                aria-current={i === active}
                onPointerDown={() => onSlideDown(i)}
                onPointerUp={() => onSlideUp(i)}
                onPointerLeave={endHold}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && i !== active) goTo(i);
                }}
                className="relative block w-full select-none outline-none"
              >
                <div
                  className={`relative h-[clamp(260px,44vh,440px)] overflow-hidden transition-all duration-1000 ease-smooth ${
                    i === active ? "opacity-100" : "opacity-35 scale-[0.94]"
                  }`}
                >
                  <img
                    src={item.img}
                    alt={item.alt}
                    draggable={false}
                    className="h-full w-full object-cover"
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                  <div
                    className={`absolute inset-0 flex items-end bg-gradient-to-t from-ink/85 via-ink/25 to-transparent transition-opacity duration-700 ${
                      i === active ? "opacity-100" : "pointer-events-none opacity-0"
                    }`}
                  >
                    <div className="pointer-events-none p-6 sm:p-8">
                      <span className="font-head text-lg text-wave">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <Link
                        to={item.to}
                        data-testid="field-active-title-link"
                        tabIndex={i === active ? 0 : -1}
                        className="pointer-events-auto mt-2 block cursor-pointer text-white transition-colors duration-500 hover:text-wave"
                      >
                        <h3 className="font-head text-2xl sm:text-4xl">{item.title}</h3>
                      </Link>
                      <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-white/75 sm:text-base">
                        {item.sub}
                      </p>
                      <Link
                        to={item.to}
                        data-testid="field-active-link"
                        tabIndex={i === active ? 0 : -1}
                        className="group pointer-events-auto mt-5 inline-flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition-colors hover:text-wave"
                      >
                        {f.more}
                        <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-12" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-12 sm:pb-32 md:px-16">
        <div className="mt-6 h-px w-full bg-white/10" aria-hidden="true">
          <div ref={barRef} data-testid="carousel-progress" className="h-px w-0 bg-wave" />
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
          {held && (
            <span
              data-testid="carousel-pause-indicator"
              className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-white/50"
            >
              <Pause size={11} /> Pause
            </span>
          )}
          {items.map((item, i) => (
            <button
              key={item.to}
              data-testid={`field-nav-${i}`}
              onClick={() => goTo(i)}
              className={`text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-500 ${
                i === active ? "text-white" : "text-white/40 hover:text-wave"
              }`}
            >
              {String(i + 1).padStart(2, "0")} {item.short || item.title.split(" ")[0]}
            </button>
          ))}
          <span className="mx-1 hidden h-4 w-px bg-white/15 sm:block" aria-hidden="true" />
          <div className="ml-auto flex gap-3">
            <button
              data-testid="field-prev-button"
              onClick={() => goTo(active - 1)}
              aria-label="Vorheriges Feld"
              className="flex h-11 w-11 items-center justify-center border border-white/20 text-white/70 transition-all duration-500 hover:border-white hover:bg-white hover:text-ink"
            >
              <ArrowLeft size={15} strokeWidth={1.5} />
            </button>
            <button
              data-testid="field-next-button"
              onClick={() => goTo(active + 1)}
              aria-label="Nächstes Feld"
              className="flex h-11 w-11 items-center justify-center border border-white/20 text-white/70 transition-all duration-500 hover:border-white hover:bg-white hover:text-ink"
            >
              <ArrowRight size={15} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
